<template>
  <div>
    <div class="row q-ma-xl">
      <div class="col">
        <div class="text-h4 q-mb-xl">Registration Confirmation</div>
        <Banner v-if="completeRegistrationError" mode="negative">
          <div>
            We encountered an error submitting your registration request. Please
            try again later or file a Feedback issue. Thanks.
          </div>
        </Banner>
        <Banner v-if="loading" mode="info">
          <div>
            Loading...
          </div>
        </Banner>
        <div v-if="!loading && completeRegistrationSuccess">
          <p class="text-center">
            <img
              src="@/assets/logo-with-title.png"
              alt="Open Links"
              width="181"
              height="152"
            />
          </p>
          <p>
            Thank you for registering and welcome to Open Links! Your account is
            now active and you can
            <a href="https://screener.openlinksgolf.com/login"
              >log into the site</a
            >.
          </p>
          <p>
            At your convenience, please visit our
            <a href="https://www.openlinksgolf.com/welcome" target="_blank"
              >new user onboarding page</a
            >, where you will find training videos and additional member
            benefits.
          </p>
          <div class="q-pa-xs">
            <q-markup-table dense flat bordered separator="cell">
              <thead>
                <tr>
                  <th colspan="6">Subscription Information</th>
                </tr>
                <tr>
                  <th class="text-left">Name</th>
                  <th class="text-left">Email</th>
                  <th class="text-right">Amount</th>
                  <th class="text-right">Start Date</th>
                  <th class="text-right">End Date</th>
                  <th class="text-right">Next Payment</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td class="text-left">{{ row.name }}</td>
                  <td class="text-left">{{ row.email }}</td>
                  <td class="text-right">{{ row.amountTotal }}</td>
                  <td class="text-right">{{ row.startDate }}</td>
                  <td class="text-right">{{ row.endDate }}</td>
                  <td class="text-right">{{ row.nextPaymentDate }}</td>
                </tr>
              </tbody>
            </q-markup-table>
          </div>
          <p class="text-caption text-grey-6">
            To receive additional golf course and golf travel content, please
            follow us on
            <a href="https://www.instagram.com/openlinksgolf">Instagram</a>,
            <a href="https://www.facebook.com/openlinksgolf/">Facebook</a>, and
            <a href="https://twitter.com/openlinksgolf1">Twitter</a>. Our
            <a href="https://www.youtube.com/@openlinksgolf">YouTube</a> channel
            contains brief training videos to help you get started.
          </p>
          <p class="text-center">
            Questions?
            <a href="https://www.openlinksgolf.com/contact">Contact Us</a>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import Banner from "@/components/Banner";
import {
  sendRegistrationConfirmationMessage,
  sendNotifyAdminRegistrationConfirmationMessage,
} from "@/apis/email-service";
import { addUser } from "@/apis/user-service";
import { addUserProfile } from "@/apis/user-profile-service";

export default {
  name: "RegisterSuccess",
  components: { Banner },
  data() {
    return {
      loading: false,
      completeRegistrationSuccess: false,
      completeRegistrationError: null,
      row: {
        name: "",
        orderNumber: "",
        registrationNumber: "",
        amountTotal: "",
        startDate: "",
        endDate: "",
        nextPaymentDate: "",
        email: "",
      },
      currencyFormatter: new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }),
      dateFormatter: new Intl.DateTimeFormat("en-US", {
        year: "numeric",
        day: "2-digit",
        month: "2-digit",
      }),
    };
  },
  mounted() {},
  computed: {
    metadata() {
      const metadata = this.getMetadata();

      return metadata;
    },
  },
  watch: {
    metadata: {
      handler(newVal) {
        if (!newVal) {
          return;
        }

        this.register(newVal);
      },
      immediate: true,
    },
  },
  methods: {
    getMetadata() {
      const infoRaw = this.$session.get("registrationInfo");

      if (!infoRaw) {
        return null;
      }

      const parsed = JSON.parse(infoRaw);

      return parsed;
    },
    register(userInfo) {
      this.$store.commit("setIsLoading", true);
      this.loading = true;

      console.debug("register", userInfo);

      return this.createUser(userInfo)
        .then(userRecord => {
          console.debug("Successfully created new user:", userRecord);

          return userRecord;
        })
        .then(userRecord => {
          const newUserInfo = {
            uid: userRecord.uid,
            ...userInfo,
          };

          return this.createUserProfile(newUserInfo).then(() => {
            console.debug(
              "Successfully created new user profile:",
              newUserInfo,
            );

            return {
              userRecord,
              userInfo,
            };
          });
        })
        .then(this.handleRegistrationSuccess)
        .then(this.sendEmailMessages)
        .catch(this.handleRegistrationError)
        .finally(() => {
          this.$store.commit("setIsLoading", false);
          this.loading = false;
        });
    },
    createUser({ email, password }) {
      const payload = {
        email,
        password,
      };

      return addUser(payload);
    },
    createUserProfile({
      uid,
      birthDate,
      city,
      country,
      coursePreference,
      displayName,
      email,
      firstName,
      handicap,
      homeCourse,
      lastName,
      password,
      phoneNumber,
      receiveEmails,
      referralSource,
      roundsPerYear,
      state,
      streetAddress,
      tripsPerYear,
      zipCode,
    }) {
      const payload = {
        uid,
        birthDate,
        city,
        country,
        coursePreference,
        displayName,
        email,
        firstName,
        handicap,
        homeCourse,
        lastName,
        password,
        phoneNumber,
        receiveEmails,
        referralSource,
        roundsPerYear,
        state,
        streetAddress,
        tripsPerYear,
        zipCode,
      };

      return addUserProfile(payload);
    },
    handleRegistrationSuccess({ userRecord, userInfo }) {
      console.debug("handleRegistrationSuccess", userRecord, userInfo);

      const { firstName, lastName, email, displayName } = userInfo;
      const name = `${firstName} ${lastName}`;
      const registrationNumber = "";
      const periodStart = new Date();
      const startDate = this.dateFormatter.format(periodStart);
      const row = {
        name,
        registrationNumber,
        amountTotal: "N/A",
        startDate,
        endDate: "N/A",
        nextPaymentDate: "N/A",
        email,
        to: email,
        displayName,
      };

      this.row = row;
      this.completeRegistrationError = false;
      this.completeRegistrationSuccess = true;

      return row;
    },
    handleRegistrationError(error) {
      console.error("handleRegistrationError", error);

      this.completeRegistrationSuccess = false;
      this.completeRegistrationError = true;
    },
    sendEmailMessages(row) {
      return sendRegistrationConfirmationMessage(row)
        .then(sendNotifyAdminRegistrationConfirmationMessage(row))
        .then(() => {
          this.sendConfirmationEmailSuccess = true;
          this.sendConfirmationEmailError = false;
        });
    },
  },
};
</script>
<style lang="scss" scoped></style>
